import { useState } from 'react'
import {
  ArrowDown,
  Check,
  Database,
  GitCommitHorizontal,
  LoaderCircle,
  RotateCcw,
  ShieldAlert,
  X,
} from 'lucide-react'
import type {
  OperatorPackageActionOutcomeDto,
  OperatorPackageActionResultDto,
  OperatorPackageProjectionDto,
  OperatorPackageReferenceDto,
  OperatorProposalDto,
  OperatorReviewActionDto,
  OperatorReviewPackageDto,
} from '../../services/operatorApi'

interface OperatorProposalPanelProps {
  proposal: OperatorProposalDto
  reviewPackage?: OperatorReviewPackageDto
  onApprove?: (
    proposalId: string,
    packageReference: OperatorPackageReferenceDto,
  ) => Promise<unknown> | unknown
  onReject?: (proposalId: string) => Promise<unknown> | unknown
  executionResult?: OperatorPackageProjectionDto
  onRetryAction?: (
    proposalId: string,
    actionId: string,
  ) => Promise<unknown> | unknown
  isApproving?: boolean
  isRejecting?: boolean
  retryingActionId?: string | null
}

const actionLabels: Record<OperatorReviewActionDto['actionType'], string> = {
  create_project: 'Create project',
  create_board: 'Create board',
  create_task: 'Create task',
  assign_owner: 'Assign owner',
  set_due_date: 'Set due date',
  create_dependency: 'Create dependency',
  merge_timeline_event: 'Merge timeline event',
}

function sentenceCase(value: string): string {
  const words = value.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function confidenceLabel(confidence: number): string {
  return `Confidence ${Math.round(confidence * 100)}%`
}

function packageImplementsProposal(
  proposal: OperatorProposalDto,
  reviewPackage: OperatorReviewPackageDto | undefined,
): boolean {
  const root = reviewPackage?.actions[0]
  if (!root) return false
  if (
    root.actionType !== proposal.proposedActionType
    || root.targetType !== proposal.targetType
    || root.targetKey !== proposal.targetKey
  ) return false
  return !(
    proposal.projectBindingId
    && reviewPackage.projectBindingId
    && proposal.projectBindingId !== reviewPackage.projectBindingId
  )
}

function targetLabel(
  target: { id: string } | { actionType: string; targetKey: string },
): string {
  return 'id' in target ? target.id : target.targetKey
}

function dateLabel(value: string | null | undefined): string {
  if (!value) return 'Not set'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function actionDetails(
  action: OperatorReviewActionDto,
): Array<[label: string, value: string]> {
  switch (action.actionType) {
    case 'create_project':
      return [
        ['Project name', action.input.name],
        ['Owner', action.input.ownerUserId],
        ['Created by', action.input.createdByUserId],
        ['Company', action.input.companyId ?? 'Not set'],
        ['Description', action.input.description ?? 'Not set'],
        ['Status', action.input.status ?? 'Not set'],
        ['Start date', dateLabel(action.input.startedAt)],
        ['Target go-live', dateLabel(action.input.targetGoLiveAt)],
      ]
    case 'create_board':
      return [
        ['Board name', action.input.name],
        ['Workspace', action.input.serverId],
        ['Preset', sentenceCase(action.input.preset)],
        ['Created by', action.input.createdByUserId],
        ['Visibility', sentenceCase(action.input.visibility ?? 'personal')],
        ['Departments', action.input.departmentIds?.join(', ') ?? 'Not set'],
        ['Icon', action.input.icon ?? 'Not set'],
        ['Color', action.input.color ?? 'Not set'],
      ]
    case 'create_task':
      return [
        ['Task title', action.input.title],
        ['Board', targetLabel(action.input.board)],
        ['Description', action.input.descriptionMd ?? 'Not set'],
        ['Status', action.input.statusId ?? 'Not set'],
        ['Owner', action.input.assigneeUserId ?? 'Unassigned'],
        ['Reporter', action.input.reporterUserId],
        ['Due date', dateLabel(action.input.dueDate)],
        ['Priority', action.input.priority ?? 'Not set'],
      ]
    case 'assign_owner':
      return [
        ['Task', targetLabel(action.input.item)],
        ['Owner', action.input.ownerUserId ?? 'Unassigned'],
      ]
    case 'set_due_date':
      return [
        ['Task', targetLabel(action.input.item)],
        ['Due date', dateLabel(action.input.dueDate)],
      ]
    case 'create_dependency':
      return [
        [
          'Dependency',
          `${targetLabel(action.input.fromItem)} → ${targetLabel(action.input.toItem)}`,
        ],
        ['Relationship', sentenceCase(action.input.kind)],
      ]
    case 'merge_timeline_event':
      return [
        ['Source event', action.input.sourceEventId],
        ['Target event', action.input.targetEventId],
        ['Evidence', action.input.evidenceIds.join(', ')],
        ['Reason', action.input.reason],
        ['Confidence', `${Math.round(action.input.confidence * 100)}%`],
        ['Impact', `${sentenceCase(action.input.impactClass)} impact`],
      ]
  }
}

function ActionRow({
  action,
  index,
  result,
  onRetry,
  isRetrying,
  retryDisabled,
}: {
  action: OperatorReviewActionDto
  index: number
  result?: OperatorPackageActionOutcomeDto | OperatorPackageActionResultDto
  onRetry?: () => void
  isRetrying: boolean
  retryDisabled: boolean
}) {
  const detailedResult = result && 'actionType' in result
    ? result
    : undefined
  return (
    <li className="relative grid grid-cols-[2rem_minmax(0,1fr)] gap-3 pb-4 last:pb-0">
      {index > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-4 left-[0.9375rem] h-4 w-px bg-border"
        />
      )}
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-md border border-border bg-background font-mono text-[0.6875rem] font-semibold tabular-nums text-muted-foreground"
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0 rounded-lg border border-border bg-background px-3.5 py-3">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {actionLabels[action.actionType]}
            </p>
            <p className="mt-0.5 truncate font-mono text-[0.6875rem] text-muted-foreground">
              {action.targetType} / {action.targetKey}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-wide text-muted-foreground">
            {result && (
              <>
                <span className="font-semibold text-foreground">
                  {sentenceCase(result.state)}
                </span>
                <span aria-hidden="true">Â·</span>
              </>
            )}
            <span>{sentenceCase(action.policyDecision)}</span>
            <span aria-hidden="true">·</span>
            <span>{sentenceCase(action.policy.impactClass)} impact</span>
            <span aria-hidden="true">·</span>
            <span>{Math.round(action.policy.confidence * 100)}%</span>
          </div>
        </div>
        <dl className="mt-3 grid gap-x-4 gap-y-2 border-t border-border pt-3 sm:grid-cols-2">
          {actionDetails(action).map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="font-mono text-[0.625rem] uppercase tracking-wide text-muted-foreground">
                {label}
              </dt>
              <dd className="mt-0.5 break-words text-xs leading-5 text-foreground">
                {value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 border-l-2 border-primary/50 pl-2.5 text-xs leading-5 text-foreground">
          {action.policyReason}
        </p>
        {result && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <div className="min-w-0 font-mono text-[0.6875rem] uppercase tracking-wide text-muted-foreground">
              {detailedResult?.errorCategory
                ? sentenceCase(detailedResult.errorCategory)
                : detailedResult?.resultType
                  ? `${sentenceCase(detailedResult.resultType)}${detailedResult.resultId ? ` Â· ${detailedResult.resultId}` : ''}`
                  : sentenceCase(result.state)}
            </div>
            {result.state === 'retryable_failure'
              && result.retryable
              && onRetry && (
              <button
                type="button"
                aria-label={`Retry ${actionLabels[action.actionType]}`}
                aria-busy={isRetrying ? 'true' : undefined}
                disabled={retryDisabled}
                onClick={onRetry}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              >
                {isRetrying ? (
                  <LoaderCircle
                    aria-hidden="true"
                    className="size-3.5 animate-spin motion-reduce:animate-none"
                  />
                ) : (
                  <RotateCcw aria-hidden="true" className="size-3.5" />
                )}
                {isRetrying ? 'Retrying actionâ€¦' : 'Retry action'}
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

export function OperatorProposalPanel({
  proposal,
  reviewPackage,
  onApprove,
  onReject,
  executionResult,
  onRetryAction,
  isApproving = false,
  isRejecting = false,
  retryingActionId = null,
}: OperatorProposalPanelProps) {
  const [localAction, setLocalAction] = useState<'approve' | 'reject' | null>(
    null,
  )
  const [actionError, setActionError] = useState<string | null>(null)
  const [localRetryActionId, setLocalRetryActionId] = useState<string | null>(
    null,
  )
  const isPending = proposal.state === 'pending'
  const activeRetryActionId = localRetryActionId ?? retryingActionId
  const busy = localAction !== null
    || activeRetryActionId !== null
    || isApproving
    || isRejecting
  const safeExecutionResult =
    executionResult?.proposalId === proposal.id
    && executionResult.workspaceId === proposal.workspaceId
      ? executionResult
      : undefined
  const packageMatches = packageImplementsProposal(proposal, reviewPackage)
  const packageAuthorized = reviewPackage?.actions.every(
    ({ policy }) => policy.isAuthorized,
  ) === true
  const approvalAvailable = Boolean(
    reviewPackage
    && packageMatches
    && packageAuthorized
    && onApprove
    && isPending,
  )

  const approve = async () => {
    if (
      busy
      || !reviewPackage
      || !packageMatches
      || !packageAuthorized
      || !onApprove
      || !isPending
    ) return
    setActionError(null)
    setLocalAction('approve')
    try {
      await onApprove(proposal.id, {
        actionPackageId: reviewPackage.actionPackageId,
        packageFingerprint: reviewPackage.packageFingerprint,
      })
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'The package could not be approved.',
      )
    } finally {
      setLocalAction(null)
    }
  }

  const reject = async () => {
    if (busy || !onReject || !isPending) return
    setActionError(null)
    setLocalAction('reject')
    try {
      await onReject(proposal.id)
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'The proposal could not be rejected.',
      )
    } finally {
      setLocalAction(null)
    }
  }

  const retry = async (actionId: string) => {
    if (busy || !onRetryAction) return
    setActionError(null)
    setLocalRetryActionId(actionId)
    try {
      await onRetryAction(proposal.id, actionId)
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'The action could not be retried.',
      )
    } finally {
      setLocalRetryActionId(null)
    }
  }

  return (
    <article
      aria-label="Operator proposal approval"
      className="overflow-hidden rounded-xl border border-border bg-card text-foreground"
    >
      <header className="border-b border-border px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Operator control ledger
            </p>
            <h2 className="mt-1.5 text-base font-semibold tracking-tight text-foreground">
              Review mutation package
            </h2>
          </div>
          <div className="flex items-stretch overflow-hidden rounded-md border border-border bg-muted/40 font-mono text-[0.6875rem] uppercase tracking-wide">
            <span className="border-r border-border px-2.5 py-1.5 text-muted-foreground">
              {sentenceCase(proposal.impactClass)} impact
            </span>
            <span className="px-2.5 py-1.5 font-semibold text-foreground">
              {confidenceLabel(proposal.confidence)}
            </span>
          </div>
        </div>
      </header>

      <div className="px-5 py-5">
        <section
          aria-labelledby={`operator-evidence-${proposal.id}`}
          className="rounded-lg border border-border bg-muted/30 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
              <Database aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 id={`operator-evidence-${proposal.id}`} className="text-sm font-semibold">
                  Evidence
                </h3>
                <span className="font-mono text-[0.6875rem] uppercase tracking-wide text-muted-foreground">
                  {proposal.evidenceLinks.length}{' '}
                  {proposal.evidenceLinks.length === 1 ? 'source record' : 'source records'}
                </span>
              </div>
              {proposal.evidenceLinks.length > 0 ? (
                <ul className="mt-3 grid gap-1.5">
                  {proposal.evidenceLinks.map((link) => (
                    <li key={link.id} className="font-mono text-[0.6875rem] text-muted-foreground">
                      {link.evidenceId}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  No authorized source records are attached.
                </p>
              )}
            </div>
          </div>
        </section>

        <div aria-hidden="true" className="flex h-8 items-center pl-[1.45rem] text-muted-foreground">
          <ArrowDown className="size-4" />
        </div>

        <section
          aria-labelledby={`operator-decision-${proposal.id}`}
          className="rounded-lg border border-border bg-muted/30 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-primary">
              <ShieldAlert aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 id={`operator-decision-${proposal.id}`} className="text-sm font-semibold">
                  Decision gate
                </h3>
                <span className="font-mono text-[0.6875rem] font-semibold uppercase tracking-wide text-primary">
                  {sentenceCase(proposal.policyDecision)}
                </span>
              </div>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {proposal.reason}
              </p>
            </div>
          </div>
        </section>

        <div aria-hidden="true" className="flex h-8 items-center pl-[1.45rem] text-muted-foreground">
          <ArrowDown className="size-4" />
        </div>

        <section
          aria-labelledby={`operator-mutations-${proposal.id}`}
          className="rounded-lg border border-border bg-muted/30 p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
              <GitCommitHorizontal aria-hidden="true" className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 id={`operator-mutations-${proposal.id}`} className="text-sm font-semibold">
                  Mutation package
                </h3>
                <span className="font-mono text-[0.6875rem] uppercase tracking-wide text-muted-foreground">
                  {reviewPackage?.actions.length ?? 0} actions
                </span>
              </div>
              {reviewPackage ? (
                <>
                  <p className="mt-3 inline-flex rounded-md border border-border bg-card px-2 py-1 font-mono text-[0.6875rem] font-semibold text-foreground">
                    {sentenceCase(reviewPackage.mode)} mode
                  </p>
                  {!packageMatches && (
                    <p className="mt-3 border-l-2 border-destructive/50 pl-3 text-xs leading-5 text-destructive">
                      This package does not implement the durable proposal.
                      Refresh before approving.
                    </p>
                  )}
                  {!packageAuthorized && (
                    <p className="mt-3 border-l-2 border-destructive/50 pl-3 text-xs leading-5 text-destructive">
                      This package contains an unauthorized mutation and cannot
                      be approved.
                    </p>
                  )}
                  {safeExecutionResult && (
                    <p
                      role="status"
                      className="mt-3 border-l-2 border-primary/50 pl-3 text-xs font-semibold leading-5 text-foreground"
                    >
                      {safeExecutionResult.state === 'partial'
                        ? 'Package partially applied'
                        : safeExecutionResult.state === 'applied'
                          ? 'Package applied'
                          : safeExecutionResult.state === 'failed'
                            ? 'Package failed'
                            : 'Package denied'}
                    </p>
                  )}
                  <ol aria-label="Mutation package" className="mt-4 list-none p-0">
                    {reviewPackage.actions.map((action, index) => {
                      const candidateResult = safeExecutionResult?.actions[index]
                      const result = candidateResult
                        && (
                          !('actionType' in candidateResult)
                          || candidateResult.actionType === action.actionType
                        )
                          ? candidateResult
                          : undefined
                      return (
                        <ActionRow
                          key={`${action.actionType}:${action.targetKey}:${index}`}
                          action={action}
                          index={index}
                          result={result}
                          onRetry={
                            result?.state === 'retryable_failure'
                            && result.retryable
                            && onRetryAction
                              ? () => void retry(result.actionId)
                              : undefined
                          }
                          isRetrying={
                            activeRetryActionId === result?.actionId
                          }
                          retryDisabled={busy}
                        />
                      )
                    })}
                  </ol>
                </>
              ) : (
                <p className="mt-3 border-l-2 border-border pl-3 text-xs leading-5 text-muted-foreground">
                  The reviewed package is not available. Refresh before
                  approving.
                </p>
              )}
            </div>
          </div>
        </section>
      </div>

      <footer className="border-t border-border bg-muted/20 px-5 py-4">
        {actionError && (
          <p
            role="alert"
            className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {actionError}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[0.6875rem] text-muted-foreground">
            Package ID · {reviewPackage?.actionPackageId ?? proposal.id}
          </p>
          <div className="flex items-center gap-2">
            {onReject && (
              <button
                type="button"
                onClick={() => void reject()}
                disabled={!isPending || busy}
                aria-busy={localAction === 'reject' || isRejecting ? 'true' : undefined}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              >
                {localAction === 'reject' || isRejecting ? (
                  <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin motion-reduce:animate-none" />
                ) : (
                  <X aria-hidden="true" className="size-3.5" />
                )}
                Reject
              </button>
            )}
            <button
              type="button"
              onClick={() => void approve()}
              disabled={!approvalAvailable || busy}
              aria-busy={localAction === 'approve' || isApproving ? 'true' : undefined}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            >
              {localAction === 'approve' || isApproving ? (
                <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin motion-reduce:animate-none" />
              ) : (
                <Check aria-hidden="true" className="size-3.5" />
              )}
              {localAction === 'approve' || isApproving
                ? 'Applying package…'
                : 'Approve package'}
            </button>
          </div>
        </div>
      </footer>
    </article>
  )
}

export default OperatorProposalPanel
